import React, { forwardRef, useMemo } from 'react';
import Badge from '@atlaskit/badge';
import Button from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/utility/chevron-down';
import { xcss, Box } from '@atlaskit/primitives';
import { PopupSelect, CheckboxOption, type OptionType } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { messages } from './messages';

type FilterButtonProps = {
	label: string;
	isSelected: boolean;
	selectedCount: number;
	testId?: string;
};

type Props = {
	onSelect: (type: string[]) => void;
	selectedTypes: string[];
	options: OptionType[];
};

const FilterButton = forwardRef<HTMLElement, FilterButtonProps>(
	({ label, selectedCount, isSelected, testId, ...triggerProps }: FilterButtonProps, ref) => (
		<Button
			{...triggerProps}
			testId={testId}
			ref={ref ?? undefined}
			isSelected={isSelected}
			iconAfter={<ChevronDownIcon label="" color={token('color.icon')} />}
		>
			{label}
			{selectedCount > 0 && (
				<Box as="span" xcss={badgeWrapperStyles}>
					<Badge appearance="primary">{selectedCount}</Badge>
				</Box>
			)}
		</Button>
	),
);

export const TaskTypeDropdownFilter = ({ onSelect, selectedTypes, options }: Props) => {
	const { formatMessage } = useIntl();

	const selectedValues = useMemo(
		() =>
			selectedTypes
				.map((selectedType) => options.find((option) => option.value === selectedType))
				.filter((option): option is OptionType => option != null),
		[selectedTypes, options],
	);
	return (
		<PopupSelect
			components={{ Option: CheckboxOption }}
			options={options}
			isMulti
			value={selectedValues}
			isSearchable={false}
			closeMenuOnSelect={false}
			hideSelectedOptions={false}
			onChange={(selectedOptions) =>
				onSelect(
					selectedOptions
						.map((option) => option.value)
						.filter((value): value is string => typeof value === 'string'),
				)
			}
			target={({ isOpen, ...triggerProps }) => (
				<FilterButton
					{...triggerProps}
					isSelected={isOpen}
					label={formatMessage(messages.allSuggestionsLabelPrefixNonFinal)}
					selectedCount={selectedTypes.length}
					testId="periscope.ui.suggestions.filter.task-type-dropdown-filter"
				/>
			)}
		/>
	);
};
const badgeWrapperStyles = xcss({
	marginLeft: 'space.050',
});
